import { memo, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { StaticImageData } from 'next/image';
import classnames from 'classnames';
import gsap from 'gsap';
import { A11y, Controller, Swiper as SwiperClass } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import styles from './TestimonialCarousel.module.scss';

import BaseImage from '@/components/BaseImage/BaseImage';
import ProgressNavigationArrow from '@/components/ProgressNavigationArrow/ProgressNavigationArrow';

import QuoteIcon from '@/components/svgs/DarkQuote.svg';

export type Props = {
  className?: string;
  title: string;
  eyebrow: string;
  data: {
    id: number;
    image: {
      data: StaticImageData;
      dataMobile: StaticImageData;
      alt: string;
    };
    quote: string;
    name: string;
  }[];
};

function TestimonialCarousel({ className, title, eyebrow, data }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const firstSwiperRef = useRef<SwiperClass | null>(null);
  const secondSwiperRef = useRef<SwiperClass | null>(null);
  const [firstSwiper, setFirstSwiper] = useState<SwiperClass | null>(null);
  const [secondSwiper, setSecondSwiper] = useState<SwiperClass | null>(null);
  const [slideCounter, setSlideCounter] = useState(1);
  const [activeSlide, setActiveSlide] = useState<number | null>(null);
  const navProgressPercentage = (slideCounter / data.length) * 100;

  const handleSlideChange = useCallback(() => {
    if (firstSwiperRef.current !== null) {
      setSlideCounter(firstSwiperRef.current.realIndex + 1);
    }
  }, []);

  useEffect(() => {
    if (activeSlide !== null) {
      const allSlides = Array.from(containerRef.current!.querySelectorAll(`.${styles.slideTwo}.swiper-slide`));
      const activeSlideEl = allSlides.splice(activeSlide, 1)[0];
      // NOTE: Handle swiper duplicated slides
      const duplicatedSlideIndex = activeSlide < data.length ? data.length + 1 : activeSlide - data.length;
      const duplicatedSlideEl = allSlides[duplicatedSlideIndex];

      const activeContentWrapper = activeSlideEl?.querySelector(`.${styles.contentWrapper}`);
      const duplicatedSlideWrapper = duplicatedSlideEl?.querySelector(`.${styles.contentWrapper}`);
      const inactiveContentWrapper = allSlides?.map((el) => el.querySelector(`.${styles.contentWrapper}`));

      gsap.to(inactiveContentWrapper, {
        opacity: 0,
        ease: 'ease01',
        duration: 0.83,
        delay: 0.03,
        overwrite: true
      });

      gsap.to([activeContentWrapper, duplicatedSlideWrapper], {
        opacity: 1,
        ease: 'ease01',
        duration: 0.83,
        delay: 0.03,
        overwrite: true
      });
    }
  }, [activeSlide, data.length]);

  useLayoutEffect(() => {
    if (secondSwiperRef.current) {
      secondSwiperRef.current.on('slideChange', (e) => {
        return setActiveSlide(e.activeIndex);
      });
    }
  }, []);

  return (
    <div className={classnames(styles.TestimonialCarousel, className)}>
      <div className={styles.headingWrapper}>
        <span className={styles.eyebrow}>{eyebrow}</span>
        <h2 className={styles.heading}>{title}</h2>
      </div>

      <div className={styles.mainWrapper} ref={containerRef}>
        <Swiper
          modules={[Controller, A11y]}
          onSwiper={setFirstSwiper}
          className={styles.carouselOne}
          controller={{ control: secondSwiper! }}
          onBeforeInit={(swiper) => {
            firstSwiperRef.current! = swiper;
          }}
          onSlideChange={handleSlideChange}
          spaceBetween={0}
          slidesPerView="auto"
          slidesOffsetBefore={0}
          speed={700}
          edgeSwipeThreshold={0}
          loop={true}
          loopedSlides={data.length}
        >
          {data.map((slide, i) => (
            <SwiperSlide key={i} className={styles.slideOne}>
              <BaseImage data={slide.image.dataMobile} className={styles.mobileImage} alt={slide.image.alt} />
              <BaseImage data={slide.image.data} alt={slide.image.alt} className={styles.desktopImage} />
            </SwiperSlide>
          ))}
        </Swiper>
        <QuoteIcon className={styles.quoteIcon} aria-hidden />
        <Swiper
          modules={[Controller, A11y]}
          onSwiper={setSecondSwiper}
          controller={{ control: firstSwiper! }}
          className={styles.carouselTwo}
          onBeforeInit={(swiper) => {
            secondSwiperRef.current! = swiper;
          }}
          onSlideChange={handleSlideChange}
          slidesPerView="auto"
          slidesOffsetBefore={0}
          speed={830}
          edgeSwipeThreshold={0}
          loop={true}
          loopedSlides={data.length}
        >
          {data.map((slide, i) => (
            <SwiperSlide key={i} className={styles.slideTwo}>
              <div className={classnames(styles.contentWrapper)}>
                <blockquote className={styles.quote}>{slide.quote}</blockquote>
                <span className={styles.name}>{slide.name}</span>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
      <ProgressNavigationArrow
        className={styles.navArrow}
        onLeftClick={() => firstSwiperRef.current?.slidePrev()}
        onRightClick={() => firstSwiperRef.current?.slideNext()}
        percentage={navProgressPercentage}
      />
    </div>
  );
}

export default memo(TestimonialCarousel);
